import { Box, Icon, IconProps, Typography } from '@branch-messenger/willow-ui'
import {
  Amex,
  Discover,
  Mastercard,
  Pencil01,
  Trash03,
  Visa,
} from '@branch-messenger/willow-ui/icons'
import { FC, use } from 'react'

import {
  AccountDetailContainer,
  AccountDetailDropdownAction,
  AccountDetailDropdownActionType,
  AccountDetailItem,
  AccountDetailSubtitle,
} from '@/components/account-detail'
import { Alert, AlertBodyText } from '@/components/alert'
import { DialogContext } from '@/context'
import { formatExpiration } from '@/lib/date-helpers'
import { useDeleteDebitCard } from '@/queries/card'
import { useOrg } from '@/selectors/organization'
import { DebitCardType } from '@/types/card'

import { DebitCardModalContent } from '../payouts/DebitCardModalContent'
import { useConnectedCardInfo } from './hooks/useConnectedCardInfo'

type ConnectedCardProps = {
  type: DebitCardType
  id?: number
  network?: string
  last4?: string
  expiration?: string
}

type IconMapItem = {
  Icon: IconProps['Icon']
}

export const iconMap: Record<NonNullable<string>, IconMapItem> = {
  visa: { Icon: Visa },
  mastercard: { Icon: Mastercard },
  discover: { Icon: Discover },
  amex: { Icon: Amex },
}

export const ConnectedCard: FC<ConnectedCardProps> = ({
  type,
  id = 0,
  network = '',
  last4 = '',
  expiration = '',
}) => {
  const {
    data: { appName },
  } = useOrg()
  const { title, icon, missingCardDescription } = useConnectedCardInfo(type)
  const iconProps = iconMap[network.toLowerCase()]

  const { mutate: deleteDebitCard } = useDeleteDebitCard()

  const { showAlert, showDialog } = use(DialogContext)

  const handleReplace = () => {
    showDialog({
      children: <DebitCardModalContent type={type} />,
      hideCloseButton: true,
    })
  }

  const handleDelete = () => {
    showAlert({
      title: 'Are you sure?',
      description: `Deleting your ${title} connected debit card will delete your current debit card information from your account and will unenroll you from instant payouts through ${appName}.`,
      actionDescription: 'Yes, Continue',
      cancelDescription: 'No, cancel',
      isDestructive: true,
      handleAction() {
        deleteDebitCard({
          accountId: id,
          type,
        })
      },
    })
  }

  const dropdownActions: AccountDetailDropdownActionType[] = [
    {
      label: 'Replace',
      Icon: Pencil01,
      onClick: handleReplace,
      titleSize: 'sm',
    },
    {
      label: 'Delete',
      Icon: Trash03,
      onClick: handleDelete,
      color: 'destructive',
      titleSize: 'sm',
    },
  ]

  const cardIcon = iconProps ? <Icon {...iconProps} size={8} /> : null

  return (
    <Box
      $display="flex"
      $direction="column"
      $gap={4}
      $align="initial"
      $fullWidth
    >
      <Typography $size="2xl" $bold>
        {title}
      </Typography>

      {id ? (
        <AccountDetailContainer>
          <AccountDetailItem
            leadingIcon={cardIcon}
            title={`${network} • ${last4}`}
            subtitle={
              <AccountDetailSubtitle $size="xs">
                {`Expires on ${formatExpiration(expiration)}`}
              </AccountDetailSubtitle>
            }
            accountDetailItemHeaderProps={{
              $gap: 1,
            }}
            action={<AccountDetailDropdownAction actions={dropdownActions} />}
          />
        </AccountDetailContainer>
      ) : (
        <Alert Icon={icon}>
          <AlertBodyText>{missingCardDescription}</AlertBodyText>
        </Alert>
      )}
    </Box>
  )
}
